import { useQuery } from '@tanstack/react-query'
import { UserService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'
import { useAuth } from './useAuth'

export const personsQueryKeys = {
  all: () => ['persons'] as const,
  lists: () => ['persons', 'list'] as const,
  list: (personIds?: string[]) => ['persons', 'list', personIds] as const,
  details: () => ['persons', 'detail'] as const,
  detail: (personId: string) => ['persons', 'detail', personId] as const,
}

const fetchAllPersons = async (): Promise<UserService.Person[]> => {
  try {
    const response = await apis.userService.personsGet()
    return response.data
  } catch (error) {
    console.error('Failed to fetch persons:', error)
    return []
  }
}

const fetchPersonsByIds = async (personIds: string[]): Promise<UserService.Person[]> => {
  if (!personIds || personIds.length === 0) {
    return []
  }

  try {
    // Fetch all persons and filter by IDs
    const allPersons = await fetchAllPersons()
    const personIdsSet = new Set(personIds)
    return allPersons.filter(person => personIdsSet.has(person.id))
  } catch (error) {
    console.error('Failed to fetch persons by IDs:', error)
    return []
  }
}

export const usePersons = (personIds?: string[]) => {
  const { isPartner, isAdmin } = useAuth()
  const enabled = (isPartner || isAdmin) && !!personIds && personIds.length > 0
  const query = useQuery({
    queryKey: personsQueryKeys.list(personIds),
    queryFn: () => {
      if (!personIds || personIds.length === 0) {
        return Promise.resolve([])
      }
      return fetchPersonsByIds(personIds)
    },
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
  })


  return {
    persons: query.data ?? [],
    isLoading: query.isPending && enabled,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  } as const
}
